"use client";

import { IconArrowLeft, IconArrowRight, IconX } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface ModalProps {
  children: React.ReactNode;
  visible: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  totalItems?: number;
  currentItemIndex?: number;
}

export const Modal: React.FC<ModalProps> = ({
  visible,
  children,
  onClose,
  onPrevious,
  onNext,
  totalItems,
  currentItemIndex,
}) => {
  const touchStartX = useRef<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const MIN_SWIPE_DISTANCE = 60;
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  // Handle escape, arrow left, arrow right.
  useEffect(() => {
    const handleKeyDown = (event: any) => {
      switch (event.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          setLoading(true);
          onPrevious?.();
          break;
        case "ArrowRight":
          setLoading(true);
          onNext?.();
          break;
      }
    };
    if (visible) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, onNext, onPrevious, onClose]);

  // Handle body scroll locking.
  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (!onPrevious && !onNext) return;
    if (!touchStartX.current) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX.current;
    setSwipeOffset(diff);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (!touchStartX.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchEndX - touchStartX.current;

    if (Math.abs(distance) >= MIN_SWIPE_DISTANCE) {
      if (distance > 0 && onPrevious) {
        setLoading(true);
        onPrevious();
      } else if (distance < 0 && onNext) {
        setLoading(true);
        onNext();
      }
    }

    touchStartX.current = null;
    setSwipeOffset(0);
  };

  useEffect(() => {
    if (visible && loading) {
      setLoading(false);
    }
  }, [visible, loading]);

  const modalRoot = typeof window !== "undefined" ? document.getElementById("modal-portal") : null;
  const portalTarget = modalRoot || (typeof window !== "undefined" ? document.body : undefined);

  const modalContent = (
    <div
      ref={modalRef}
      className={`fixed inset-0  bg-black/70 dark:bg-black/60 flex items-center justify-center z-[100] transition-all duration-200 ${
        visible
          ? "opacity-100 backdrop-blur-md pointer-events-auto"
          : "opacity-0 backdrop-blur-none pointer-events-none"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        handleTouchStart(e);
      }}
      onTouchMove={(e) => {
        e.stopPropagation();
        handleTouchMove(e);
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        handleTouchEnd(e);
      }}
    >
      {onPrevious && (
        <IconArrowLeft
          className="fixed rounded-full size-8 p-1 left-4 z-[110] top-1/2
          border-r border-r-white/25 border-b border-b-white/25
         shadow-[inset_-2px_-4px_16px_rgba(255,255,255,0.2)]
         bg-neutral-600/40 backdrop-blur-sm 
         text-white/80"
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
        />
      )}
      {onNext && (
        <IconArrowRight
          className="fixed rounded-full size-8 p-1 right-4 z-[110] top-1/2 
          border-l border-l-white/25 border-b border-b-white/25  
         shadow-[inset_2px_-4px_16px_rgba(255,255,255,0.2)]
         bg-neutral-600/40 backdrop-blur-sm 
         text-white/80"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
        />
      )}
      <IconX
        className="fixed rounded-full size-8 p-1 right-4 top-4 z-[110] 
        border-b border-b-white/20  
         shadow-[inset_1px_-4px_8px_rgba(255,255,255,0.1)]
         bg-neutral-600/40 backdrop-blur-sm
         text-white/80"
      />
      {visible && totalItems && totalItems > 1 && currentItemIndex !== undefined && (
        <div
          className="fixed bottom-16 left-1/2 -translate-x-1/2 flex space-x-1.5 z-[105] pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {Array.from({ length: totalItems }).map((_, index) => (
            <span
              key={index}
              className={`block w-1.5 h-1.5 rounded-full bg-white ${
                index === currentItemIndex ? "opacity-100" : "opacity-70"
              }`}
            />
          ))}
        </div>
      )}
      {visible && (
        <div
          className="max-w-full max-h-svh overflow-hidden shadow-black shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: `translateX(${
              (swipeOffset > 0 && onPrevious) || (swipeOffset < 0 && onNext) ? swipeOffset : 0
            }px)`,
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 max-w-screen max-h-screen overflow-auto"
            style={{ display: loading ? "none" : "block" }}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );

  return portalTarget ? ReactDOM.createPortal(modalContent, portalTarget) : null;
};
